"use client";

import { Building2, Users, Trophy, Star } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";

import { Link } from "@/i18n/navigation";
import { OrganizationData } from "@/entities/organization/model/types";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { Card, CardContent } from "@/shared/ui/cards/card";
import { SafeBanner } from "@/shared/ui/data-display/safe-banner";
import { Avatar, AvatarImage, AvatarFallback } from "@/shared/ui/data-display/avatar";
import { EmptyState } from "@/widgets/shared/empty-state";

interface OrganizationListProps {
  organizations: OrganizationData[];
  onLoadMore?: (() => void) | undefined;
  hasMore?: boolean | undefined;
}

export function OrganizationList({
  organizations,
  onLoadMore,
  hasMore,
}: OrganizationListProps) {
  const tUi = useTranslations("Ui");
  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: "200px",
  });

  const didTriggerRef = useRef(false);

  useEffect(() => {
    if (!isIntersecting) {
      didTriggerRef.current = false;
      return;
    }
    if (!hasMore || !onLoadMore) return;
    if (didTriggerRef.current) return;
    didTriggerRef.current = true;
    onLoadMore();
  }, [isIntersecting, hasMore, onLoadMore]);

  if (organizations.length === 0) {
    return (
      <EmptyState
        title={tUi("organizationList.organizatsiiNeNaydeny")}
        description={tUi("organizationList.poprobuyteIzmenitParametryPoiska")}
        icon={
          <Building2 className="h-12 w-12 text-muted-foreground" aria-hidden />
        }
      />
    );
  }

  return (
    <div className="flex-1 space-y-4 pb-16 pt-0">
      {organizations.map((org, index) => (
        <Link
          key={org.id}
          href={`/organizations/${org.url || org.id}`}
          prefetch={false}
        >
          <Card className="group relative flex flex-col overflow-hidden border border-border/40 bg-muted transition-all duration-150 hover:-translate-y-0.5 hover:border-primary/40 hover:bg-primary/5 hover:shadow-md md:flex-row">
            <div className="absolute inset-0 z-0 overflow-hidden">
              <SafeBanner
                src={org.banner}
                alt={org.name}
                fill
                priority={index === 0}
                rounded="rounded-none"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/50 dark:bg-black/60" />
            </div>

            <CardContent className="relative z-10 flex flex-1 flex-col p-4 gap-4 items-start md:flex-row md:items-center">
              <div className="flex items-center gap-3 flex-1 min-w-0 w-full">
                <Avatar className="size-12 shrink-0 ring-2 ring-background/60">
                  <AvatarImage src={org.avatar} alt={org.name} />
                  <AvatarFallback>
                    <Building2 className="size-6 text-muted-foreground" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-lg font-bold leading-tight text-foreground">
                      {org.name}
                    </h2>
                    {org.isVerified && (
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 shrink-0" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {org.description || tUi("litOrganizationList.opisanieOtsutstvuet")}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground pt-1">
                    <div
                      className="flex items-center gap-1.5"
                      title={tUi("organizationList.uchastniki")}
                    >
                      <Users className="h-4 w-4" />
                      <span className="font-semibold text-foreground">
                        {org.memberCount ?? 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5" title={tUi("organizationList.turniry")}>
                      <Trophy className="h-4 w-4" />
                      <span className="font-semibold text-foreground">
                        {org.stats.totalTournaments}
                      </span>
                    </div>
                    <div
                      className="flex items-center gap-1.5"
                      title={tUi("organizationList.podpischiki")}
                    >
                      <span className="text-base leading-none">★</span>
                      <span className="font-semibold text-foreground">
                        {org.subscriberCount ?? 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {org.upcomingTournaments &&
                org.upcomingTournaments.length > 0 && (
                  <div className="w-full rounded border border-primary/20 bg-primary/5 p-2 text-xs md:w-auto md:min-w-[200px] md:shrink-0">
                    <p className="text-primary font-semibold mb-0.5">
                      {tUi("organizationList.blizhayshiyTurnir")}
                    </p>
                    <p className="font-medium text-foreground truncate max-w-[180px]">
                      {org.upcomingTournaments[0]?.name}
                    </p>
                    <p className="text-muted-foreground">
                      {org.upcomingTournaments[0]?.startDate ??
                        tUi("litOrganizationList.dataNeNaznachena")}
                    </p>
                  </div>
                )}
            </CardContent>
          </Card>
        </Link>
      ))}
      {onLoadMore && <div ref={ref} className="h-4 w-full" />}
    </div>
  );
}
